// ==================== 统一上传入口（自适应策略）====================
// 业务层只需调用 uploadFile(file, options)，内部根据文件大小自动选择：
//   file.size < 5MB  → 普通上传（单次请求）
//   file.size >= 5MB → 分片上传流程（哈希 → init → 逐片 → complete）
//                      init 返回 fallback_simple / instant / resume / new 四种状态
//
// 进度回调 onProgress(percent, info) 的 info.mode 取值：
//   'simple' | 'hashing' | 'instant' | 'multipart'

import { calcFileHash } from './fileHash'
import { getExt } from './fileSize'
import {
    uploadFileSimple,
    multipartInit,
    multipartPart,
    multipartComplete,
    multipartAbort
} from '@/api/upload'

// 分片阈值：小于此值走普通上传（单位：字节）
const MULTIPART_THRESHOLD = 5 * 1024 * 1024 // 5MB

// 取消错误标记
const ABORT_CODE = 'UPLOAD_ABORTED'

function isAborted(signal) {
    return signal && signal.aborted
}

function throwIfAborted(signal) {
    if (isAborted(signal)) {
        const err = new Error('上传已取消')
        err.code = ABORT_CODE
        throw err
    }
}

// 构造普通上传的 FormData
function buildSimpleFormData(file, categoryId) {
    const formData = new FormData()
    formData.append('file', file)
    if (categoryId) formData.append('category_id', categoryId)
    return formData
}

// 普通上传
async function simpleUpload(file, categoryId, onProgress, signal) {
    const formData = buildSimpleFormData(file, categoryId)
    const res = await uploadFileSimple(formData, (e) => {
        if (e.total && typeof onProgress === 'function') {
            const percent = Math.round((e.loaded / e.total) * 100)
            onProgress(percent, { mode: 'simple' })
        }
    })
    return res.data
}

// 分片上传（含秒传 / 断点续传 / 降级）
async function multipartUpload(file, categoryId, onProgress, signal) {
    // 1. 计算文件哈希
    onProgress?.(0, { mode: 'hashing', message: '计算文件指纹...' })
    const fileHash = await calcFileHash(file)
    throwIfAborted(signal)

    // 2. 初始化任务
    const initRes = await multipartInit({
        file_name: file.name,
        file_size: file.size,
        file_hash: fileHash,
        file_ext: getExt(file.name),
        category_id: categoryId
    })
    throwIfAborted(signal)

    const { upload_mode } = initRes.data

    // 3a. 降级：当前存储驱动不支持分片
    if (upload_mode === 'fallback_simple') {
        const { max_size, max_size_display } = initRes.data
        if (file.size > max_size) {
            const err = new Error(`当前为本地存储模式，最大支持上传 ${max_size_display} 的文件`)
            err.code = 'OVER_LIMIT'
            throw err
        }
        return await simpleUpload(file, categoryId, onProgress, signal)
    }

    // 3b. 秒传：文件已存在，直接返回
    if (upload_mode === 'instant') {
        onProgress?.(100, { mode: 'instant', message: '秒传完成' })
        return initRes.data.file
    }

    // 3c. 断点续传 / 新任务
    const { upload_id, part_size, total_parts, uploaded_parts } = initRes.data
    const uploadedSet = new Set((uploaded_parts || []).map(p => p.partNumber))

    try {
        // 4. 逐片上传（跳过已完成分片），建议串行避免后端并发写入问题
        for (let i = 1; i <= total_parts; i++) {
            throwIfAborted(signal)
            if (uploadedSet.has(i)) continue

            const start = (i - 1) * part_size
            const end = Math.min(start + part_size, file.size)
            const chunk = file.slice(start, end)

            const formData = new FormData()
            formData.append('upload_id', upload_id)
            formData.append('part_number', i)
            formData.append('part', chunk)

            await multipartPart(formData)
            onProgress?.(Math.round((i / total_parts) * 100), {
                mode: 'multipart',
                currentPart: i,
                totalParts: total_parts
            })
        }

        // 5. 完成合并
        throwIfAborted(signal)
        const completeRes = await multipartComplete(upload_id)
        return completeRes.data
    } catch (err) {
        // 非取消错误时尝试清理已上传分片，避免占用存储
        if (err.code !== ABORT_CODE && upload_id) {
            multipartAbort(upload_id).catch(() => {})
        }
        throw err
    }
}

/**
 * 统一上传入口（自适应）
 * @param {File} file - 浏览器 File 对象
 * @param {Object} [options]
 * @param {number} [options.categoryId=0] - 分类ID
 * @param {AbortSignal} [options.signal] - 取消信号，调用 controller.abort() 中断上传
 * @param {(percent: number, info: { mode: string, [k: string]: any }) => void} [options.onProgress] - 进度回调
 * @returns {Promise<Object>} 文件信息 { file_id, file_url, instant? }（秒传时多返回 instant 标记）
 */
export function uploadFile(file, options = {}) {
    const { categoryId = 0, signal, onProgress } = options
    if (file.size < MULTIPART_THRESHOLD) {
        return simpleUpload(file, categoryId, onProgress, signal)
    }
    return multipartUpload(file, categoryId, onProgress, signal)
}

// 导出取消标记，供组件判断错误类型
export const ABORT_ERROR_CODE = ABORT_CODE

// 导出用于取消的控制器工厂
export function createUploadController() {
    return new AbortController()
}
